import {Button} from "@/components/ui/button.tsx";
import {FormEvent, useContext, useEffect, useRef, useState} from "react";
import {useParams} from "react-router";
import axios from "axios";
import {toast} from "sonner";
import {FolderIcon, FolderUpIcon, LoaderCircle, Plus, PlusIcon} from "lucide-react";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion.tsx";
import {FormContext} from "@/components/form/Form.tsx";

import {
    Dialog, DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"

import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs"
import AttachmentCard from "@/components/editor/AttachmentCard.tsx";

export default function AttachmentEditor({attachments, setAttachments, reloadAttachments}: {
    attachments: Attachment[],
    setAttachments: (attachments: Attachment[]) => void,
    reloadAttachments: () => void
}) {
    const params = useParams();
    const documentId = params.id;
    const [uploading, setUploading] = useState(false);

    const [files, setFiles] = useState<any>({});
    const [path, setPath] = useState<string>('');

    useEffect(() => {
        axios.get("/api/files", {
            params: {
                path: path
            }
        }).then((res) => {
            const allowedExtensions = ["pdf", "png", "jpg", "jpeg"]
            const filterFiles = res.data?.files.filter((item: string) => {
                const extension = item.split('.').pop()?.toLowerCase() ?? "";
                return allowedExtensions.includes(extension);
            })

            setFiles({...res.data, files: filterFiles ?? []});
        }).catch((err) => {
            console.error(err)
        })
    }, [path]);


    const [positionClicked, setPositionClicked] = useState("none");

    async function uploadAttachment(e: FormEvent<HTMLInputElement>) {
        try {
            setUploading(true);
            if (!e.currentTarget.files) return;
            if (!documentId) return;
            const file = e.currentTarget.files[0];
            const api_resource = await axios.post("/api/attachment", {
                name: file.name,
                position: positionClicked ?? "none",
                document_id: documentId
            })
            if (api_resource.status !== 200) {
                toast.error('Hiba történt a fájl feltöltése során!');
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_token', api_resource.data.upload_token);
            axios.post(import.meta.env.VITE_ATTACHMENT_UPLOAD_URL, formData).then(res => {
                if (res.status !== 200) {
                    console.log(res.data)
                    toast.error('Hiba történt a feltöltés során!');
                    return;
                }
                setAttachments([...attachments, {
                    file_name: res.data.filename,
                    id: api_resource.data.id,
                    size: file.size,
                    type: file.type,
                    position: positionClicked,
                    document_id: documentId,
                    created_at: new Date(),
                    updated_at: new Date()
                }]);
                toast.success('Sikeresen feltöltve!');
            }).catch(err => {
                if (err.response.status === 413) {
                    toast.error('A fájl túl nagy!');
                }
                toast.error('Hiba történt a feltöltés során!');
            }).finally(() => {
                setUploading(false);
                reloadAttachments();
            })
        } catch (e: any) {
            console.log(e)
            toast.error("Hiba történt a fájl feltöltése során!");
            setUploading(false);
        }
    }

    async function attachExisting(path: string) {
        axios.post("/api/attachment/public", {
            document_id: documentId,
            path: path,
            position: positionClicked
        }).then((res) => {
            setAttachments([...attachments, res.data])
            toast.success("Sikeresen csatolva!")
        }).catch((err) => {
            console.error(err)
            toast.error("Hiba történt a fájl csatolása során!")
        })
    }

    function deleteAttachment(id?: string) {
        if (id === null) {
            axios.post("/api/ai-report/stop", {
                document_id: documentId,
            }).then(res => {
                if (res.status !== 200) {
                    toast.error('Hiba történt a törlés során!');
                    return;
                }
                setAttachments(attachments.filter(a => a.id !== id));
                toast.success('Sikeresen törölve!');
            })
        } else {
            axios.delete(`/api/attachment/${id}`).then(res => {
                if (res.status !== 200) {
                    toast.error('Hiba történt a törlés során!');
                    return;
                }
                setAttachments(attachments.filter(a => a.id !== id));
                toast.success('Sikeresen törölve!');
            })
        }
    }

    const fileInput = useRef<HTMLInputElement>(null);

    const form = useContext(FormContext)
    const positions = [
        {
            name: "Lojalitás",
            value: "lojalitas"
        },
        {
            name: "Tőkeképzés",
            value: "tokekepzes"
        },
        {
            name: "Szindikátus",
            value: "szindikatus"
        },
        {
            name: "Generációváltás",
            value: "generacio"
        },
        {
            name: "Nyugdíj",
            value: "nyugdij"
        },
        {
            name: "Törzsgárda",
            value: "torzsgarda"
        },
        {
            name: "Cégértékelő",
            value: "ai"
        },
        {
            name: "Nincs téma",
            value: "none"
        }
    ].filter(x => (form.getValue("theme")?.[x.value] ?? false) || x.value == "none");

    return (<>
        <Accordion type={"multiple"}
                   defaultValue={positions.map(pos => (attachments.filter(a => (a.position ?? "none") === pos.value).length > 0 || pos.value == "none") ? pos.value : null).filter(x => x !== null)}>
            {positions.map((position) => (<>
                <AccordionItem value={position.value}>
                    <AccordionTrigger>{position.name}</AccordionTrigger>
                    <AccordionContent>
                        <div className={"grid grid-cols-2 gap-4"}>
                            {attachments.filter(x => (x.position ?? "none") == position.value).map((attachment) => (
                                    <AttachmentCard attachment={attachment} deleteAttachment={deleteAttachment}/>
                                )
                            )}


                            <Dialog>
                                <DialogTrigger asChild>
                                    <div
                                        onClick={() => setPositionClicked(position.value)}
                                        className={"border-dashed border-2 rounded-xl border-muted-foreground h-26 flex items-center justify-center cursor-pointer"}>
                                        {uploading ? <LoaderCircle className={"animate-spin text-muted-foreground"}/> :
                                            <Plus className={"text-muted-foreground"}/>}
                                    </div>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Csatolmány kiválasztása</DialogTitle>
                                    </DialogHeader>

                                    <Tabs defaultValue="public" className="">
                                        <TabsList className="grid w-full grid-cols-2">
                                            <TabsTrigger value="public">Dokumentumtár</TabsTrigger>
                                            <TabsTrigger value="private">Saját fájl</TabsTrigger>
                                        </TabsList>
                                        <TabsContent value="public">
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle>Dokumentumtár</CardTitle>
                                                    <CardDescription>
                                                        Feltöltött fájlok csatolása a Dokumentumtárból.
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-1">
                                                    <div
                                                        className={"inline-flex gap-2 items-center p-1 cursor-pointer hover:text-primary"}
                                                        onClick={() => setPath(path.split("/").slice(0, -1).join('/'))}
                                                    >
                                                        <FolderUpIcon
                                                            className={"size-4"}/>..
                                                    </div>
                                                    <div
                                                        className={"max-h-[300px] overflow-y-auto flex flex-col gap-1"}>
                                                        {
                                                            files.directories?.map((dir: string) => (
                                                                <div
                                                                    className={"inline-flex gap-2 items-center p-1 cursor-pointer hover:text-primary"}
                                                                    onClick={() => setPath(dir)}
                                                                >
                                                                    <FolderIcon
                                                                        className={"size-4"}/>{dir.split('/').splice(-1).at(0)}
                                                                </div>
                                                            ))
                                                        }
                                                        {
                                                            files.files?.map((file: string) => (
                                                                <DialogClose asChild>
                                                                    <div
                                                                        className={"inline-flex gap-2 hover:text-primary cursor-pointer p-1"}
                                                                        onClick={() => attachExisting(file)}
                                                                    >{file.split('/').splice(-1).at(0)}</div>
                                                                </DialogClose>
                                                            ))
                                                        }
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </TabsContent>
                                        <TabsContent value="private">
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle>Saját fájl</CardTitle>
                                                    <CardDescription>
                                                        Töltsön fel saját fájlokat a számítógépéről.
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-2">
                                                    <DialogClose asChild>
                                                        <div
                                                            className={"w-full border-sidebar rounded-md cursor-pointer border-2 border-dashed inline-flex gap-2 items-center justify-center p-1"}
                                                            onClick={() => {
                                                                setPositionClicked(position.value)
                                                                fileInput.current?.showPicker()
                                                            }}>
                                                            <PlusIcon/> Fájl kiválasztása
                                                        </div>
                                                    </DialogClose>
                                                </CardContent>
                                            </Card>
                                        </TabsContent>
                                    </Tabs>

                                    <DialogFooter>
                                        <DialogClose asChild>
                                            <Button>Bezárás</Button>
                                        </DialogClose>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                        </div>
                    </AccordionContent>
                </AccordionItem>
            </>))}
        </Accordion>
        <input className={"hidden"} type={'file'} ref={fileInput} onChange={(e) => uploadAttachment(e)}/>
    </>)
}